import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Gift, Search, Smartphone, Star, Wifi } from "lucide-react";
import { gauntlet, ApiError, type CatalogProduct } from "../lib/api";
import { cx, formatFiat, timeAgo } from "../lib/format";
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  Input,
  Panel,
  Skeleton,
} from "../components/ui";

type ProductType = "gift_card" | "esim" | "topup";

const PRODUCTS: {
  id: ProductType;
  label: string;
  blurb: string;
  Icon: typeof Gift;
  category?: string;
}[] = [
  {
    id: "gift_card",
    label: "Gift cards",
    blurb: "Thousands of brands across 170+ countries.",
    Icon: Gift,
    category: "all-gift-cards",
  },
  {
    id: "esim",
    label: "eSIM data",
    blurb: "Mobile data abroad without a local SIM.",
    Icon: Wifi,
    category: "esim",
  },
  {
    id: "topup",
    label: "Mobile top-up",
    blurb: "Credit for a prepaid number, anywhere.",
    Icon: Smartphone,
    category: "refill",
  },
];

/** Countries the catalog is most often browsed in. */
const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "IN", name: "India" },
  { code: "BR", name: "Brazil" },
];

/**
 * Spend crypto on gift cards, eSIMs and top-ups.
 *
 * The product grid is **ours** — Gauntlet fetches the catalog through its own
 * server (the upstream sends no CORS headers) and renders brands in its own
 * design system. Only the final payment step hands off to Bitrefill's checkout,
 * deep-linked to the chosen product and themed to match.
 *
 * The catalog endpoint is undocumented, so `available: false` is a normal
 * outcome rather than an error: the page falls back to the plain embed, which
 * is documented and supported.
 */
export function SpendPage() {
  const queryClient = useQueryClient();

  const [productType, setProductType] = useState<ProductType>("gift_card");
  const [country, setCountry] = useState("US");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [chosen, setChosen] = useState<CatalogProduct | null>(null);
  const [embed, setEmbed] = useState<{
    url: string;
    origin: string;
    orderId: number;
  } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Debounce so typing does not fire a request per keystroke at someone else's
  // endpoint.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const activeCategory = PRODUCTS.find((p) => p.id === productType)?.category;

  const { data: catalog, isLoading: loadingCatalog } = useQuery({
    queryKey: ["spend-catalog", country, debounced, activeCategory],
    queryFn: () =>
      gauntlet.spendCatalog({
        country,
        ...(debounced ? { q: debounced } : {}),
        ...(activeCategory ? { category: activeCategory } : {}),
        limit: 48,
      }),
    staleTime: 10 * 60 * 1000,
    enabled: !embed,
  });

  const { data: detail } = useQuery({
    queryKey: ["spend-product", chosen?.slug],
    queryFn: () => gauntlet.spendProduct(chosen!.slug),
    enabled: Boolean(chosen),
  });

  const { data: orderData } = useQuery({
    queryKey: ["spend-orders"],
    queryFn: () => gauntlet.spendOrders(),
    refetchInterval: embed ? 5_000 : false,
  });

  const start = useMutation({
    mutationFn: (slug?: string) =>
      gauntlet.startSpend({
        productType,
        preferredChain: "bitcoin",
        theme: "dark",
        country,
        ...(slug ? { slug } : {}),
      }),
    onSuccess: (result) => {
      setError(null);
      if (result.status === "ready") {
        setEmbed({
          url: result.embed.url,
          origin: result.embed.expectedOrigin,
          orderId: result.orderId,
        });
      } else {
        setStatus(result.reason);
        void queryClient.invalidateQueries({ queryKey: ["approvals"] });
      }
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not start"),
  });

  /**
   * Relay the embed's events to the server.
   *
   * The origin check is the security boundary — without it any page could post
   * a forged `invoice_complete` and mark an order delivered.
   */
  useEffect(() => {
    if (!embed) return;

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== embed.origin) return;

      const payload = event.data as Record<string, unknown> | null;
      if (!payload || typeof payload["event"] !== "string") return;

      void gauntlet
        .spendEvent(embed.orderId, payload)
        .then((result) => {
          setStatus(result.status);
          if (result.status === "delivered" || result.status === "failed") {
            void queryClient.invalidateQueries({ queryKey: ["spend-orders"] });
          }
        })
        .catch(() => {
          // A relay failure must not break the purchase in the frame.
        });

      if (payload["event"] === "payment_intent") {
        setStatus("Payment requested — approve it in your wallet");
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embed, queryClient]);

  const orders = orderData?.orders ?? [];

  // --- checkout ------------------------------------------------------------

  if (embed) {
    return (
      <div className="space-y-5">
        <button
          onClick={() => {
            setEmbed(null);
            setStatus(null);
            setChosen(null);
          }}
          className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink"
        >
          <ArrowLeft size={15} />
          Back to browsing
        </button>

        <Panel title={chosen ? `Buy ${chosen.name}` : "Checkout"} padded={false}>
          {status && (
            <div className="border-b border-hairline px-5 py-2.5 text-[13px] text-brass">
              {status}
            </div>
          )}
          {/*
            Sandboxed deliberately. allow-scripts and allow-forms are what the
            checkout needs; allow-same-origin lets it reach its own API. With a
            strict origin check on every message, that is the narrowest
            configuration that still works.
          */}
          <iframe
            ref={frameRef}
            src={embed.url}
            title="Checkout"
            className="h-[660px] w-full rounded-b-[var(--radius-panel)]"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            referrerPolicy="no-referrer"
          />
        </Panel>
      </div>
    );
  }

  // --- product detail ------------------------------------------------------

  if (chosen) {
    return (
      <div className="mx-auto max-w-lg space-y-5">
        <button
          onClick={() => setChosen(null)}
          className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink"
        >
          <ArrowLeft size={15} />
          All brands
        </button>

        <Panel>
          <div className="flex items-start gap-4">
            <BrandTile product={chosen} size={64} />
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold">{chosen.name}</h1>
              {chosen.priceRange && (
                <p className="num mt-0.5 text-sm text-ink-dim">{chosen.priceRange}</p>
              )}
              {chosen.rating !== undefined && (
                <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink-faint">
                  <Star size={11} className="text-brass" fill="currentColor" />
                  {chosen.rating.toFixed(1)}
                  {chosen.reviews ? ` · ${chosen.reviews} reviews` : ""}
                </p>
              )}
            </div>
          </div>

          {detail?.available && detail.packages.length > 0 && (
            <div className="mt-5 space-y-2">
              <p className="label-xs">Available denominations</p>
              <div className="flex flex-wrap gap-1.5">
                {detail.packages.slice(0, 24).map((pkg) => (
                  <span
                    key={pkg.value}
                    className="num rounded-lg border border-hairline bg-abyss px-2.5 py-1 text-sm"
                  >
                    {detail.currency === "GBP" ? "£" : detail.currency === "EUR" ? "€" : "$"}
                    {pkg.value}
                  </span>
                ))}
              </div>
              <p className="text-xs text-ink-faint">
                Pick the exact amount on the next screen — the checkout controls that step.
              </p>
            </div>
          )}

          {detail?.outOfStock && (
            <Callout tone="warn" title="Out of stock">
              This brand is temporarily unavailable.
            </Callout>
          )}

          <div className="mt-5">
            <Button
              variant="primary"
              size="lg"
              full
              loading={start.isPending}
              disabled={detail?.outOfStock}
              onClick={() => start.mutate(chosen.slug)}
            >
              Continue to payment
            </Button>
          </div>

          {error && (
            <div className="mt-3">
              <Callout tone="danger">{error}</Callout>
            </div>
          )}

          {detail?.terms && (
            <p className="mt-4 line-clamp-4 text-xs leading-relaxed text-ink-faint">
              {detail.terms}
            </p>
          )}
        </Panel>
      </div>
    );
  }

  // --- browse --------------------------------------------------------------

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Spend</h1>
        <p className="mt-1 text-sm text-ink-dim">
          Turn crypto into gift cards, mobile data and phone credit.
        </p>
      </div>

      <Callout tone="success" title="Recommended — crypto refills">
        The smoothest way to spend your coins is a gift-card{' '}
        <strong>refill</strong>: pay with crypto, get a code instantly — no
        account, no KYC. Browse the full catalogue at{' '}
        <a
          href="https://cryptorefills.com"
          target="_blank"
          rel="noreferrer noopener"
          className="font-medium text-brass no-underline hover:underline"
        >
          cryptorefills.com
        </a>
        , or pick a category below.
      </Callout>

      <div className="grid gap-3 sm:grid-cols-3">
        {PRODUCTS.map(({ id, label, blurb, Icon }) => (
          <button
            key={id}
            onClick={() => setProductType(id)}
            className={cx(
              "panel p-4 text-left transition-colors",
              productType === id
                ? "border-brass/50 bg-brass-wash/40"
                : "hover:border-hairline-bright",
            )}
          >
            <Icon className={productType === id ? "text-brass" : "text-ink-dim"} size={20} />
            <p className="mt-2.5 text-sm font-medium">{label}</p>
            <p className="mt-0.5 text-xs text-ink-faint">{blurb}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint"
          />
          <Input
            placeholder="Search brands…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="h-10 rounded-lg border border-hairline bg-abyss px-3 text-sm text-ink"
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {status && (
        <Callout tone="warn" title="Waiting for approval">
          {status}
        </Callout>
      )}
      {error && <Callout tone="danger">{error}</Callout>}

      {loadingCatalog ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : catalog?.available && catalog.products.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {catalog.products.map((product) => (
              <button
                key={product.slug}
                onClick={() => setChosen(product)}
                className="panel group flex flex-col items-center gap-2.5 p-4 text-center transition-colors hover:border-brass/40"
              >
                <BrandTile product={product} size={48} />
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-medium">{product.name}</p>
                  {product.priceRange && (
                    <p className="num mt-0.5 truncate text-[11px] text-ink-faint">
                      {product.priceRange}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
          <p className="text-center text-xs text-ink-faint">
            Showing {catalog.products.length} of {catalog.totalCount} in{" "}
            {COUNTRIES.find((c) => c.code === country)?.name ?? country}
          </p>
        </>
      ) : (
        // The catalog is an undocumented upstream, so being unavailable is a
        // normal outcome. Offer the supported path rather than an error.
        <Panel>
          <EmptyState
            icon={<Gift size={28} />}
            title={
              catalog?.available === false
                ? "Brand list unavailable right now"
                : "No brands match that search"
            }
            description={
              catalog?.available === false
                ? "You can still buy through the standard checkout — it just does not preview brands here first."
                : "Try a different search or country."
            }
            action={
              catalog?.available === false ? (
                <Button
                  variant="primary"
                  loading={start.isPending}
                  onClick={() => start.mutate(undefined)}
                >
                  Open checkout
                </Button>
              ) : undefined
            }
          />
        </Panel>
      )}

      <Callout tone="info" title="No account, no API key, no KYC">
        Purchases are paid straight from your wallet. Gauntlet holds no balance with the provider
        and ships no credentials — and a failed order refunds itself to an address you control.
      </Callout>

      {orders.length > 0 && (
        <Panel title="Orders" padded={false}>
          <ul className="divide-y divide-hairline">
            {orders.map((order) => (
              <li key={order.id} className="flex items-center gap-3 px-5 py-3.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{order.product_name}</span>
                    {order.profile_name && <Badge>{order.profile_name}</Badge>}
                  </div>
                  <p className="text-xs text-ink-faint">
                    {order.product_type.replace("_", " ")} · {timeAgo(order.created_at)}
                  </p>
                </div>
                {Number(order.fiat_amount_cents) > 0 && (
                  <span className="num text-sm">{formatFiat(order.fiat_amount_cents)}</span>
                )}
                <Badge
                  tone={
                    order.status === "delivered"
                      ? "gain"
                      : order.status === "failed" || order.status === "cancelled"
                        ? "loss"
                        : "warn"
                  }
                >
                  {order.status.replace("_", " ")}
                </Badge>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

/**
 * A brand tile.
 *
 * The catalog ships an inline base64 preview, so a 48-item grid renders with
 * zero extra image requests — then the CDN logo swaps in. Falls back to the
 * brand's own colours and initial if neither loads, which keeps the grid even
 * rather than pocked with broken images.
 */
function BrandTile({ product, size }: { product: CatalogProduct; size: number }) {
  const [failed, setFailed] = useState(false);
  const preview = product.iconPreview ?? product.logoPreview;
  const source = failed ? undefined : (product.logoUrl ?? preview);

  if (!source) {
    return (
      <div
        className="flex shrink-0 items-center justify-center rounded-xl font-semibold"
        style={{
          width: size,
          height: size,
          background: product.iconBackground ?? "var(--color-raised)",
          color: product.iconForeground ?? "var(--color-ink)",
          fontSize: size * 0.4,
        }}
      >
        {product.name.slice(0, 1).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={source}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setFailed(true)}
      className="shrink-0 rounded-xl object-contain"
      style={{
        width: size,
        height: size,
        background: product.iconBackground ?? "var(--color-raised)",
      }}
    />
  );
}
